import { useEffect, useMemo, useState } from "react";
import { Search, ChevronLeft, Loader2, Check, Filter } from "lucide-react";
import { api } from "../../lib/api";
import { humanizeTopic, humanizeQuestion } from "./humanize";

/**
 * CRTargetingPanel — 2-stage Advanced-Targeting picker modelled on
 * PureSpectrum's own buyer UX (see spec screenshots):
 *
 *   Stage 1  "Browse topics"
 *     • Empty prompt at the top explaining what to do.
 *     • Type-ahead search over the ~1,100-entry PS attribute catalog,
 *       plus a category filter chip strip.
 *     • Each row shows a **humanized** topic title (via humanize.js) and a
 *       small hint count. Clicking a row advances to Stage 2.
 *
 *   Stage 2  "Configure topic"
 *     • Shows the humanized "I'm looking for people that…" sentence.
 *     • Multi-select checklist of the topic's options.
 *     • "Apply targeting" button commits the quota; "Back" returns to Stage 1.
 *
 * When `prefetchedData` is passed in from `StepAudience`, we skip the API
 * fetch and render immediately (the drawer's slide-in is already 300ms so
 * every ms saved on first-open matters).
 */
export default function CRTargetingPanel({ value = [], onChange, prefetchedData = null, testid = "cr-targeting-panel" }) {
  const [loading, setLoading] = useState(!prefetchedData);
  const [error, setError] = useState(null);
  const [data, setData] = useState(prefetchedData);
  const [stage, setStage] = useState("browse"); // "browse" | "configure"
  const [activeTopicId, setActiveTopicId] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [pendingOptionIds, setPendingOptionIds] = useState([]);

  useEffect(() => {
    if (prefetchedData) {
      setData(prefetchedData);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/campaigns/demographics");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) {
          const detail = e.response?.data?.detail;
          setError(detail?.code === "demographics_unavailable"
            ? "The programmatic panel's advanced targeting library is offline right now. Try again in a moment."
            : (detail || e.message || "Failed to load"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [prefetchedData]);

  const topics = data?.flat || [];

  // Group by humanized category — kept because the PS spec kept them, but
  // rendered as horizontal filter chips (not the noisy sidebar) so the
  // browse view stays clean.
  const categoryList = useMemo(() => {
    const set = new Set();
    for (const t of topics) if (t.category) set.add(t.category);
    return ["All", ...Array.from(set).sort()];
  }, [topics]);

  // Humanize + filter + search the topic list for Stage 1.
  const filteredTopics = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cat = activeCategory && activeCategory !== "All" ? activeCategory : null;
    const seen = new Set();
    const out = [];
    for (const t of topics) {
      if (cat && t.category !== cat) continue;
      const title = humanizeTopic(t.question || "");
      // Dedupe by title so multiple PS entries that boil down to the same
      // brief topic (e.g. "Country of Birth" in en / fr localizations)
      // don't stutter in the list.
      const key = title.toLowerCase();
      if (seen.has(key)) continue;
      if (q) {
        const hay = (title + " " + (t.category || "")).toLowerCase();
        if (!hay.includes(q)) continue;
      }
      seen.add(key);
      out.push({ ...t, _title: title });
      if (out.length >= 250) break;  // safety cap for very broad searches
    }
    return out;
  }, [topics, search, activeCategory]);

  const activeTopic = useMemo(() => {
    if (!activeTopicId) return null;
    return topics.find((t) => String(t.demographicId) === String(activeTopicId)) || null;
  }, [topics, activeTopicId]);

  const openTopic = (topic) => {
    const existing = (value || []).find((q) => String(q.demographicId) === String(topic.demographicId));
    setPendingOptionIds((existing?.demographicOptionIds || []).map(String));
    setActiveTopicId(topic.demographicId);
    setStage("configure");
  };

  const togglePendingOpt = (optId) => {
    setPendingOptionIds((prev) =>
      prev.includes(String(optId))
        ? prev.filter((x) => x !== String(optId))
        : [...prev, String(optId)],
    );
  };

  const applyTargeting = () => {
    if (!activeTopic) return;
    const filtered = (value || []).filter((q) => String(q.demographicId) !== String(activeTopic.demographicId));
    const next = pendingOptionIds.length > 0
      ? [...filtered, { demographicId: String(activeTopic.demographicId), demographicOptionIds: pendingOptionIds.map(String) }]
      : filtered;
    onChange(next);
    setStage("browse");
    setActiveTopicId(null);
    setPendingOptionIds([]);
  };

  const clearAndBack = () => {
    // Also remove any existing quota for this topic if the user emptied it.
    if (activeTopic && pendingOptionIds.length === 0) {
      const filtered = (value || []).filter((q) => String(q.demographicId) !== String(activeTopic.demographicId));
      onChange(filtered);
    }
    setStage("browse");
    setActiveTopicId(null);
    setPendingOptionIds([]);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-[var(--rl-text-secondary)]" data-testid={`${testid}-loading`}>
        <Loader2 className="w-4 h-4 animate-spin" /> Loading targeting library…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-sm text-[var(--rl-accent)] leading-relaxed" data-testid={`${testid}-error`}>
        {typeof error === "string" ? error : "Targeting library unavailable."}
      </div>
    );
  }

  // ============ STAGE 2 · Configure ============
  if (stage === "configure" && activeTopic) {
    const prompt = humanizeQuestion(activeTopic.question || "");
    const title = humanizeTopic(activeTopic.question || "");
    return (
      <div className="p-5 space-y-5" data-testid={`${testid}-configure`}>
        <button
          type="button"
          onClick={clearAndBack}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)] hover:text-[var(--rl-brand)] transition-colors"
          data-testid={`${testid}-back`}
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back to topics
        </button>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-[var(--rl-text-secondary)]">
            {activeTopic.category || "Advanced"}
          </div>
          <div className="mt-1 font-display font-black text-lg text-[var(--rl-text)]">
            {title}
          </div>
        </div>
        <div className="text-center py-4 border-y border-[var(--rl-border)]">
          <div className="text-sm font-semibold text-[var(--rl-text)]" data-testid={`${testid}-humanized-prompt`}>
            {prompt}
          </div>
        </div>
        {activeTopic.options?.length > 0 ? (
          <div className="max-h-[320px] overflow-y-auto border border-[var(--rl-border)]" data-testid={`${testid}-options`}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--rl-border)] bg-[var(--rl-bg-subtle)]">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[var(--rl-text-secondary)]">
                Pick one or more
              </div>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-bold">
                <button
                  type="button"
                  onClick={() => setPendingOptionIds(activeTopic.options.map((o) => String(o.demographicOptionId)))}
                  className="text-[var(--rl-brand)] hover:underline"
                  data-testid={`${testid}-select-all`}
                >Select all</button>
                <button
                  type="button"
                  onClick={() => setPendingOptionIds([])}
                  className="text-[var(--rl-text-secondary)] hover:text-[var(--rl-accent)]"
                  data-testid={`${testid}-clear-all`}
                >Clear</button>
              </div>
            </div>
            {activeTopic.options.map((o) => {
              const id = String(o.demographicOptionId);
              const isSel = pendingOptionIds.includes(id);
              return (
                <label
                  key={id}
                  className={"flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer border-b border-[var(--rl-border)] last:border-b-0 hover:bg-[var(--rl-bg-subtle)] " + (isSel ? "bg-[var(--rl-bg-subtle)]" : "")}
                  data-testid={`${testid}-opt-${id}`}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => togglePendingOpt(id)}
                    className="w-4 h-4 accent-[var(--rl-brand)]"
                  />
                  <span className="flex-1">{o.value}</span>
                  {isSel && <Check className="w-3.5 h-3.5 text-[var(--rl-brand)]" />}
                </label>
              );
            })}
          </div>
        ) : (
          <div className="text-sm italic text-[var(--rl-text-secondary)] p-4 border border-dashed border-[var(--rl-border)] text-center">
            This topic doesn&apos;t have preset options — it uses a range or free-text input that isn&apos;t editable from the drawer.
          </div>
        )}
        {activeTopic.options?.length > 0 && (
          <button
            type="button"
            onClick={applyTargeting}
            disabled={pendingOptionIds.length === 0}
            className="w-full py-3 bg-[var(--rl-brand)] text-white text-sm uppercase tracking-[0.25em] font-bold shadow-[3px_3px_0_var(--rl-text)] hover:-translate-y-px transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            data-testid={`${testid}-apply`}
          >
            Apply targeting · {pendingOptionIds.length} selected
          </button>
        )}
      </div>
    );
  }

  // ============ STAGE 1 · Browse topics ============
  return (
    <div className="flex flex-col h-full" data-testid={`${testid}-browse`}>
      {/* Empty-state prompt above search — the "tell the user what to do" spec */}
      <div className="px-5 py-6 text-center border-b border-[var(--rl-border)] bg-[var(--rl-bg-subtle)]">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">
          Advanced targeting library
        </div>
        <div className="mt-2 text-base font-semibold text-[var(--rl-text)]" data-testid={`${testid}-empty-prompt`}>
          Pick an advanced topic to add it to your targeting.
        </div>
        <div className="mt-1 text-[11px] text-[var(--rl-text-secondary)] leading-snug">
          Search a topic below, then choose who to include. Everything you pick tightens the eligible pool in real time.
        </div>
      </div>
      {/* Search + category filter */}
      <div className="px-5 pt-4 pb-3 space-y-3 border-b border-[var(--rl-border)]">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rl-text-secondary)] pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Start typing… e.g. income, industry, homeowner"
            className="w-full bg-white border border-[var(--rl-border)] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rl-brand)]"
            data-testid={`${testid}-search`}
          />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
          <Filter className="w-3.5 h-3.5 text-[var(--rl-text-secondary)] flex-shrink-0" />
          {categoryList.map((cat) => {
            const isActive = (activeCategory || "All") === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={
                  "flex-shrink-0 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors " +
                  (isActive
                    ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]"
                    : "bg-white text-[var(--rl-text)] border-[var(--rl-border)] hover:border-[var(--rl-text)]")
                }
                data-testid={`${testid}-cat-${cat.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>
      {/* Topic list */}
      <div className="flex-1 overflow-y-auto" data-testid={`${testid}-topic-list`}>
        {filteredTopics.length === 0 && (
          <div className="p-6 text-sm italic text-[var(--rl-text-secondary)] text-center" data-testid={`${testid}-no-results`}>
            No topics match &ldquo;{search}&rdquo; in {activeCategory || "All"}.
          </div>
        )}
        {filteredTopics.map((t) => {
          const applied = (value || []).some((q) => String(q.demographicId) === String(t.demographicId));
          return (
            <button
              key={t.demographicId}
              type="button"
              onClick={() => openTopic(t)}
              className={
                "w-full flex items-center gap-3 px-5 py-3 text-left border-b border-[var(--rl-border)] hover:bg-[var(--rl-bg-subtle)] transition-colors " +
                (applied ? "bg-[var(--rl-bg-subtle)]" : "")
              }
              data-testid={`${testid}-topic-${t.demographicId}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[var(--rl-text)] truncate">{t._title}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-[var(--rl-text-secondary)] mt-0.5">
                  {t.category || "Advanced"} · {(t.options?.length || 0)} options
                </div>
              </div>
              {applied && (
                <span className="text-[9px] uppercase tracking-[0.25em] font-bold text-[var(--rl-brand)] border border-[var(--rl-brand)] px-1.5 py-0.5">
                  Applied
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
